import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira, fmtDate } from "@/lib/format";
import { friendlyError, saveLenderToken, lenderTokenFor, todayLocal, setPostAuth } from "@/lib/askThemes";
import { useAuth } from "@/context/AuthContext";
import { BankBox } from "@/pages/ask/PublicAsk";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function ClaimLoan({ ask, token, lt }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const l = ask.lend;
  useEffect(() => {
    if (params.get("claim") && user && lt) { setOpen(true); params.delete("claim"); setParams(params, { replace: true }); }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lt) return <p data-testid="lend-claim-other-device" className="text-xs text-muted-foreground">To track this loan in Debii, open this link on the device you used to respond.</p>;
  const goAuth = (mode) => { setPostAuth(`/ask/${token}?claim=1`); navigate(`/auth?mode=${mode}`); };
  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/asks/lend/link", { token, lender_token: lt });
      toast.success(data.already ? "This loan is already in your Debii" : "Loan added to your Debii");
      navigate(`/debt/${data.debt_id}`);
    } catch (e) { toast.error(friendlyError(apiError(e))); setOpen(false); } finally { setBusy(false); }
  };
  return (
    <>
      {user ? (
        <Button data-testid="lend-link-to-my-debii" onClick={() => setOpen(true)} className="h-11 w-full rounded-xl">Add this loan to my Debii</Button>
      ) : (
        <div data-testid="lend-claim-auth" className="space-y-2">
          <p className="text-xs text-muted-foreground">Track it in your own Debii — free. You'll come straight back here.</p>
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="lend-claim-register" onClick={() => goAuth("register")} className="h-11 rounded-xl">Create account</Button>
            <Button data-testid="lend-claim-signin" variant="outline" onClick={() => goAuth("login")} className="h-11 rounded-xl">Sign in</Button>
          </div>
        </div>
      )}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="lend-claim-consent" className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Add this loan to your Debii?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll be recorded as the lender of {fmtNaira(l.amount, ask.currency)} to {ask.requester_name}, due {fmtDate(l.repay_date)}. These agreed terms can't be changed here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="lend-claim-confirm" disabled={busy} onClick={(e) => { e.preventDefault(); claim(); }} className="rounded-xl">Yes, add it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function CounterForm({ ask, onSubmit, busy }) {
  const [amount, setAmount] = useState(String(ask.lend.amount || ""));
  const [date, setDate] = useState(ask.lend.repay_date || "");
  const [note, setNote] = useState("");
  return (
    <div data-testid="lend-counter-form" className="space-y-2 rounded-2xl border border-border bg-card p-4">
      <p className="text-xs font-semibold text-muted-foreground">Your proposal</p>
      <Input data-testid="lend-counter-amount" placeholder="Amount (₦)" aria-label="Amount" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} className="h-12 rounded-xl" />
      <Input data-testid="lend-counter-date" aria-label="Repay by" type="date" min={todayLocal()} value={date} onChange={(e) => setDate(e.target.value)} className="h-12 rounded-xl" />
      <Input data-testid="lend-counter-note" placeholder="Note (optional)" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} className="h-12 rounded-xl" />
      <Button data-testid="lend-counter-submit" disabled={busy} onClick={() => onSubmit({ action: "counter", counter_amount: Number(amount), counter_repay_date: date, note })} className="h-11 w-full rounded-xl">Send proposal</Button>
    </div>
  );
}

export default function PublicLend({ ask, token, code, onChange }) {
  const [name, setName] = useState(ask.lend.lender_name || ask.recipient_name || "");
  const [busy, setBusy] = useState(false);
  const [countering, setCountering] = useState(false);
  const [declineArmed, setDeclineArmed] = useState(false);
  const lt = lenderTokenFor(token);
  const headers = code ? { "X-Ask-Code": code } : {};
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); onChange(); } catch (e) { const x = apiError(e); toast.error(friendlyError(x)); } finally { setBusy(false); }
  };
  const respond = (body) => run(async () => {
    const { data } = await api.post(`/ask/${token}/respond`, { ...body, lender_name: name || null, lender_token: lt }, { headers });
    saveLenderToken(token, data.lender_token);
    setCountering(false);
  });
  const sent = () => run(() => api.post(`/ask/${token}/sent`, { lender_token: lt }, { headers }));
  const l = ask.lend;
  const terms = <p className="text-sm">{ask.requester_name} would like to borrow <b>{fmtNaira(l.amount, ask.currency)}</b> and repay by <b>{fmtDate(l.repay_date)}</b>.</p>;

  if (ask.is_owner) return (
    <div data-testid="public-lend-own-request" className="rounded-2xl bg-secondary p-5 text-center space-y-1">
      <p className="font-display text-lg font-bold">This is your borrowing request</p>
      <p className="text-sm text-muted-foreground">Share it with someone who may be able to help.</p>
      <Link data-testid="public-own-manage" to="/asks" className="mt-2 inline-block text-sm font-bold text-accent">Manage in My Asks</Link>
    </div>
  );
  if (ask.status === "declined") return <div data-testid="public-lend-declined" className="rounded-2xl bg-secondary p-5 text-center text-sm">You declined this request.</div>;
  if (ask.status === "closed") return <div data-testid="public-lend-closed" className="rounded-2xl bg-secondary p-5 text-center text-sm">This request was closed by {ask.requester_name}.</div>;
  if (ask.status === "countered") return <div data-testid="public-lend-countered" className="rounded-2xl bg-amber-50 p-5 text-center text-sm">Your proposal ({fmtNaira(l.counter?.amount, ask.currency)} by {fmtDate(l.counter?.repay_date)}) is waiting for {ask.requester_name}.</div>;
  if (ask.status === "recorded") return (
    <div data-testid="public-lend-recorded" className="rounded-2xl bg-emerald-50 p-5 text-center space-y-3">
      <p className="font-display text-lg font-bold">All set ✅</p>
      <p className="text-sm text-muted-foreground">{ask.requester_name} confirmed receiving {fmtNaira(l.amount, ask.currency)}, due {fmtDate(l.repay_date)}.</p>
      <ClaimLoan ask={ask} token={token} lt={lt} />
    </div>
  );
  if (ask.status === "sent") return <div data-testid="public-lend-sent" className="rounded-2xl bg-secondary p-5 text-center text-sm">Thanks! Waiting for {ask.requester_name} to confirm receiving it.</div>;
  if (ask.status === "accepted") return lt ? (
    <div className="space-y-3">
      {terms}
      {ask.bank && <BankBox bank={ask.bank} />}
      <p className="text-[11px] text-muted-foreground">Accepting doesn't mean money has moved. Tap below only after you've made the transfer.</p>
      <Button data-testid="lend-sent-button" disabled={busy} onClick={sent} className="h-14 w-full rounded-2xl text-base font-bold">I've sent it 💸</Button>
    </div>
  ) : <div className="rounded-2xl bg-secondary p-5 text-center text-sm">This request has already been answered.</div>;

  return (
    <div data-testid="public-lend-respond" className="space-y-3">
      {terms}
      <Input data-testid="lend-lender-name" placeholder="Your name" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-xl" />
      <Button data-testid="accept-lend-request-button" disabled={busy} onClick={() => respond({ action: "accept" })} className="h-12 w-full rounded-xl">Accept</Button>
      <div className="grid grid-cols-2 gap-2">
        <Button data-testid="counter-lend-request-button" variant="outline" disabled={busy} onClick={() => setCountering((v) => !v)} className="h-11 rounded-xl">Propose different</Button>
        <Button data-testid="decline-lend-request-button" variant="outline" disabled={busy} onClick={() => (declineArmed ? respond({ action: "decline" }) : setDeclineArmed(true))} className={`h-11 rounded-xl ${declineArmed ? "border-red-300 text-red-600" : ""}`}>{declineArmed ? "Tap to confirm" : "Decline"}</Button>
      </div>
      {countering && <CounterForm ask={ask} onSubmit={respond} busy={busy} />}
    </div>
  );
}
